import {
  ApplicationRoutesNgService,
  Route,
} from '../core/routeGuard/applicationRoutesNg.service';
import {
  JwtPayload,
  TokenKeeper,
} from '../../../../../../../../frontend/libs/shared/src/lib/services/tokenKeeper';
import { Features } from '../../../../../../../../frontend/libs/shared/src/lib/types/features';

import { isSSO } from '../../../../../../../../frontend/libs/shared/src/lib/utils/account';

// I would contend that putting this preference of cards vs. list view into the URL is misguided. It's
// reasonable if some other pages needed to specify one vs. the other, but they don't. Instead it's something
// which could easily be remembered in a cookie or localStorage and the page adjusts to show one vs. the other
// as needed. It shouldn't appear in the URLs.
let view = 'cards';

export default class AptitudeNavService {
  Route = Route;

  constructor(
    private $filter,
    private applicationRoutesNgService: ApplicationRoutesNgService,
  ) {
    'ngInject';
  }

  getEnvironmentInfo(account) {
    return {
      orgName: account.organization.name,
      username: account.emailAddress,
      revisionNumber: account.environment.revisionNumber,
      buildNumber: account.environment.buildNumber,
      projectVersion: account.environment.projectVersion,
      inDevMode: account.environment.inDevMode,
      databaseName: account.environment.environmentName,
    };
  }

  getOfferLink(featureList): string {
    if (featureList.OFFER_NEW_SIO_PAGE_PROD_OUTAGE) {
      return Route.RECOMMENDED_SELLER_INITAITED_OFFER;
    }
    return '/aptitude/static/home.html#/list/SELLER_OFFER';
  }

  // Note: After playing with this for a little while, my current solution is to allow for tests at the item level
  // and the menu item level below that. An item without a test is automatically included in the Nav, one with a
  // test is only included if the test returns true.
  getLeftItems(account, featureList, tokenPayload: JwtPayload) {
    let navPathWithToggleValueFilter = this.$filter('navPathWithToggleValue');

    let isBuyer = () => account && account.isBuyer;
    let isSeller = () => account && account.isSeller;
    const isGPO = () =>
      (account && account.isGPO) || tokenPayload.isNonAptitudeUser();

    const isBuyerSideNavigationAllowed = () => isBuyer() || isGPO();
    // The existing code for this is a pooch *(%& of combining two different internal data structures, some test
    // code, and then a bunch more tests and flags only applied when the menu is constructed in the HTML. That makes
    // no damn sense.
    //
    // Construct it in one place and leave the code as easy to follow as you can. Every menu item is right here and
    // every test for every menu item is *right here*.
    return [
      {
        test: isBuyerSideNavigationAllowed,
        text: `<a><i class="apt-bars-icon fa-lg" aria-hidden="true"></i></a>`,
        menu: [
          {
            rule: true,
            header: 'aptitude marketplace',
          },
          {
            url: '/aptitude/static/platform/home/landing',
            text: 'Home',
          },
          {
            url: '/aptitude/static/platform/home/list/BUYER_REQUEST',
            text: 'Create',
            subUrls: [
              {
                url: '/aptitude/static/platform/home/list/BUYER_REQUEST',
                text: 'Categories',
              },
              {
                url: `/aptitude/static/platform/rfp/create?buyerId=${account.organization.id}`,
                text: 'Custom RFP',
              },
            ],
          },
          {
            url: navPathWithToggleValueFilter(
              '/aptitude/static/home.html#/execute/view/list/filter/activeOpportunities',
            ),
            text: 'Execute',
            subUrls: [
              {
                url: navPathWithToggleValueFilter(
                  '/aptitude/static/home.html#/execute/view/list/filter/inflight',
                ),
                text: 'Inflight',
              },
              {
                url: navPathWithToggleValueFilter(
                  '/aptitude/static/home.html#/execute/view/list/filter/sio',
                ),
                text: 'Seller Initiated Offers',
              },
              {
                url: navPathWithToggleValueFilter(
                  '/aptitude/static/home.html#/execute/view/list/filter/joinGroupContract',
                ),
                text: 'Join Contracts',
                hasPermission: account.isPartner, // This should only display if it is a partner
              },
              {
                url:
                  '/aptitude/static/home.html#/execute/view/list/filter/draftRfps',
                text: 'Draft RFPs',
              },
              {
                url:
                  '/aptitude/static/home.html#/execute/view/list/filter/requestHistory',
                text: 'Request History',
              },
            ],
          },
          {
            url: this.applicationRoutesNgService.get(Route.MANAGE_PATH),
            text: 'Manage',
            subUrls: [
              {
                url: this.applicationRoutesNgService.get(Route.MANAGE_PATH),
                queryParams: 'filter=active',
                text: 'Active Contracts',
              },
              {
                url: this.applicationRoutesNgService.get(Route.MANAGE_PATH),
                queryParams: 'filter=expired',
                text: 'Expired Contracts',
              },
            ],
          },
          {
            url: '/aptitude/static/platform/home/categories',
            text: 'Catalog',
          },
        ],
      },
      {
        test: isSeller,
        text: `<a><i class="apt-bars-icon fa-lg" aria-hidden="true"></i></a>`,
        menu: [
          {
            rule: true,
            header: 'aptitude',
          },
          {
            url: '/aptitude/static/platform/home/landing',
            text: 'Home',
          },
          {
            url: this.getOfferLink(featureList),
            text: 'Offer',
          },
          {
            url: this.applicationRoutesNgService.get(Route.RESPOND_SELLER),
            text: 'Respond',
          },
          {
            url: '/aptitude/static/home.html#/list/SELLER_ACCEPT',
            text: 'Execute',
          },
          {
            url: this.applicationRoutesNgService.get(Route.MANAGE_PATH),
            text: 'Manage',
            subUrls: [
              {
                url: this.applicationRoutesNgService.get(Route.MANAGE_PATH),
                queryParams: 'filter=active',
                text: 'Active Contracts',
              },
              {
                url: this.applicationRoutesNgService.get(Route.MANAGE_PATH),
                queryParams: 'filter=expired',
                text: 'Expired Contracts',
              },
            ],
          },
          {
            url: `/aptitude/static/platform/home/categories`,
            text: 'Catalog',
          },
          {
            rule: false,
            header: 'company market activity',
          },
        ],
      },
    ];
  }

  getRightItems(account, featureList, tokenPayload: JwtPayload) {
    let isBuyer = () => account && account.isBuyer;
    let isSeller = () => account && account.isSeller;
    const isGPO = () =>
      (account && account.isGPO) || tokenPayload.isNonAptitudeUser();

    return [
      {
        text:
          '<a>Settings <i class="apt-cog-icon fas fa-lg" aria-hidden="true"></i></a>',
        menu: [
          {
            rule: true,
            header: 'company settings',
          },
          // Show the old page regardless
          {
            test: () => account.isSeller,
            url: '/aptitude/seller/SellerManagePricing',
            text: 'Catalog',
          },
          {
            url: `/aptitude/static/platform/organization/settings/manageUsers`,
            text: 'Manage Users',
          },
          {
            test: () => (isBuyer() || isGPO()) && !account.isLeader,
            url: '/aptitude/buyer/EditBuyerProfile',
            text: 'Company Profile',
          },
          {
            test: () => isSeller() && !isGPO(),
            url: '/aptitude/seller/EditSellerProfile',
            text: 'Company Profile',
          },
          {
            test: () =>
              account.isBuyer && (account.isPartner || account.isLeader),
            url: '/aptitude/static/groupProfile.html',
            text: 'Group Profile',
          },
          // Visible for partners and solo users but not leaders
          {
            test: () => (isBuyer() || isGPO()) && !account.isLeader,
            url: '/aptitude/static/categoryPerformance.html',
            text: 'Target',
          },
          {
            test: () => isBuyer() || isGPO(),
            url: '/aptitude/static/agreements.html',
            text: 'Agreements',
          },
          {
            test: isSeller,
            url: '/aptitude/static/agreements.html',
            text: 'Agreements',
          },
          {
            test: () => isBuyer() || isGPO(),
            url: '/aptitude/static/orgsList.html#/sellers',
            text: 'Sellers Directory',
          },
          {
            test: () => isSeller(),
            url: '/aptitude/static/orgsList.html#/buyers',
            text: 'Buyers Directory',
          },
          {
            rule: true,
            header: `my settings`,
          },
          {
            test: () => isBuyer() || isSeller() || isGPO(),
            url: '/aptitude/static/myAccount.html',
            text: 'My Account',
          },
          {
            test: () => account.numberOfOrgsUserBelongsTo > 1,
            url: isSSO(account)
              ? '/aptitude/static/platform/signIn/protected'
              : '/aptitude/static/platform/signIn/choose-organization?switch-accounts=true',
            text: 'Switch Accounts',
          },
          {
            click: () => {
              window.location.href =
                '/aptitude/static/platform/signIn/sign-out';
            },
            text: 'Sign Out',
          },
        ],
      },
    ];
  }
}
